import React from 'react';
import { LABELS, MAX_LINES, visibleFields } from './itAppRules';

const optionsOf = (map) => Object.entries(map).map(([value, label]) => <option key={value} value={value}>{label}</option>);

function FieldError({ id, message }) {
  if (!message) return null;
  return <p id={id} className="ita-field-error" role="alert">{message}</p>;
}

function Field({ name, label, error, hint, children }) {
  const errId = `ita-${name}-err`;
  const hintId = hint ? `ita-${name}-hint` : undefined;
  const describedBy = [error ? errId : null, hintId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={`form-group ita-field${error ? ' has-error' : ''}`}>
      <label htmlFor={`ita-${name}`}>{label}</label>
      {children({ id: `ita-${name}`, 'aria-invalid': error ? true : undefined, 'aria-describedby': describedBy })}
      {hint && <p id={hintId} className="ita-hint">{hint}</p>}
      <FieldError id={errId} message={error} />
    </div>
  );
}

/** Editable list of one-line entries (add, remove, reorder), capped at MAX_LINES. */
function LineList({ name, label, itemLabel, addLabel, values, onChange, error }) {
  const set = (i, v) => onChange(values.map((x, j) => (j === i ? v : x)));
  const remove = (i) => onChange(values.filter((_, j) => j !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= values.length) return;
    const next = [...values];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const errId = `ita-${name}-err`;
  return (
    <fieldset className={`ita-lines${error ? ' has-error' : ''}`} aria-describedby={error ? errId : undefined}>
      <legend>{label}</legend>
      {values.length > 0 && (
        <ol className="ita-lines-list">
          {values.map((v, i) => (
            // Index keys: lines have no identity of their own and reorder swaps values.
            <li key={i} className="ita-line">
              <input
                type="text"
                className="ita-line-input"
                aria-label={`${itemLabel} ${i + 1}`}
                value={v}
                maxLength={300}
                onChange={(e) => set(i, e.target.value)}
              />
              <button type="button" className="ita-icon-btn" aria-label={`Move ${itemLabel.toLowerCase()} ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
              <button type="button" className="ita-icon-btn" aria-label={`Move ${itemLabel.toLowerCase()} ${i + 1} down`} disabled={i === values.length - 1} onClick={() => move(i, 1)}>↓</button>
              <button type="button" className="ita-icon-btn" aria-label={`Remove ${itemLabel.toLowerCase()} ${i + 1}`} onClick={() => remove(i)}>×</button>
            </li>
          ))}
        </ol>
      )}
      <button
        type="button"
        className="btn btn-secondary btn-small"
        disabled={values.length >= MAX_LINES}
        onClick={() => onChange([...values, ''])}
      >
        {addLabel}
      </button>
      {values.length >= MAX_LINES && <p className="ita-hint">Up to {MAX_LINES} lines.</p>}
      <FieldError id={errId} message={error} />
    </fieldset>
  );
}

/** The editable drawer form. Pure: state lives in ItAppDrawer. */
export default function ItAppForm({ form, errors, isCreate, onChange }) {
  const v = visibleFields(form);
  const set = (field) => (e) => onChange({ ...form, [field]: e.target.value });
  const text = (name, label, opts = {}) => (
    <Field name={name} label={label} error={errors[name]} hint={opts.hint}>
      {(p) => <input type="text" {...p} value={form[name] || ''} maxLength={opts.max || 200} onChange={set(name)} />}
    </Field>
  );
  const select = (name, label, map, opts = {}) => (
    <Field name={name} label={label} error={errors[name]} hint={opts.hint}>
      {(p) => <select {...p} value={form[name] || ''} disabled={opts.disabled} onChange={set(name)}>{optionsOf(map)}</select>}
    </Field>
  );
  const area = (name, label) => (
    <Field name={name} label={label} error={errors[name]}>
      {(p) => <textarea {...p} rows={3} value={form[name] || ''} maxLength={2000} onChange={set(name)} />}
    </Field>
  );

  return (
    <div className="ita-form">
      {text('name', 'Name')}
      {isCreate
        ? text('key', 'Key', { max: 40, hint: 'You cannot change the key after saving.' })
        : (
          <div className="form-group ita-field">
            <span className="ita-static-label">Key</span>
            <code className="ita-key">{form.key}</code>
          </div>
        )}
      <div className="ita-grid">
        {select('company', 'Company', LABELS.company)}
        {select('it_role', 'IT role', LABELS.it_role)}
        {text('tool_owner', 'Tool owner')}
        {text('it_owner', 'IT owner')}
        {text('admin_console', 'Admin console', { max: 500 })}
      </div>
      <div className="ita-grid">
        {v.signInLocked
          ? select('sign_in', 'Sign-in', LABELS.sign_in, { disabled: true, hint: 'Google Workspace is the identity provider.' })
          : select('sign_in', 'Sign-in', LABELS.sign_in)}
        {select('billing', 'Billing', LABELS.billing)}
        {v.billedBy && select('billed_by', 'Billed by', LABELS.billed_by)}
        {v.notBilled && (
          <div className="form-group ita-field">
            <span className="ita-static-label">Billed by</span>
            <span className="ita-static">Not billed</span>
          </div>
        )}
        {v.partnerName && text('partner_name', 'Partner name')}
      </div>
      {text('close_action', 'Close action', { max: 300, hint: 'One line, shown on the offboarding row.' })}
      <LineList
        name="offboarding_steps"
        label="Offboarding steps"
        itemLabel="Offboarding step"
        addLabel="Add step"
        values={form.offboarding_steps || []}
        error={errors.offboarding_steps}
        onChange={(lines) => onChange({ ...form, offboarding_steps: lines })}
      />
      {area('seat_note', 'Seat note')}
      <LineList
        name="what_it_covers"
        label="What it covers"
        itemLabel="Covers line"
        addLabel="Add line"
        values={form.what_it_covers || []}
        error={errors.what_it_covers}
        onChange={(lines) => onChange({ ...form, what_it_covers: lines })}
      />
      {area('notes', 'Notes')}
    </div>
  );
}
